// frontend/src/pages/farm/object/editValue.ts
/**
 * HOF-626 — pure helpers behind the inline property editor. No React, no I/O, so the
 * de-DE number grammar and the editability predicate are testable in isolation.
 */
import type { LenseField } from '../../../services/twinService';

export type EditInputKind = 'text' | 'number' | 'date' | 'select' | 'boolean';
export type ParseResult =
    | { ok: true; value: unknown }
    | { ok: false; reason: 'empty' | 'not_a_number' | 'not_an_integer' | 'invalid_date' | 'not_allowed' };

const FLOAT_TYPES = new Set(['float64', 'float', 'number', 'currency']);
const INT_TYPES = new Set(['int32', 'int64', 'integer']);
const BOOL_TYPES = new Set(['bool', 'boolean']);

export function editInputKind(field: LenseField): EditInputKind {
    if (field.values && field.values.length > 0) return 'select';
    if (FLOAT_TYPES.has(field.type) || INT_TYPES.has(field.type)) return 'number';
    if (field.type === 'date') return 'date';
    if (BOOL_TYPES.has(field.type)) return 'boolean';
    return 'text';
}

/**
 * Fail closed: no server flag ⇒ no badge (an older backend during a rolling deploy).
 *
 * HOF-696 D20: a protected field (`owner_only`, the backend's OWNER_ONLY_ATTRIBUTES)
 * is editable by a seat holding `twin.write.protected` — owner and farm_manager —
 * which is exactly what the backend checks. It used to read the legacy
 * `account_type === 'farmer'`, which hid the pencil from a farm_manager the
 * backend would have let write.
 */
export function isEditableRow(field: LenseField | undefined, ctx: { canWriteProtected: boolean }): boolean {
    if (!field || field.editable !== true) return false;
    if (field.owner_only && !ctx.canWriteProtected) return false;
    return true;
}

/**
 * HOF-696 D20 — the per-field value labels the lens serves (`LenseField.value_labels`),
 * merged under the caller's labels (the lens `feed_groups` discriminator map keeps
 * precedence for its key). Undefined when there is nothing to label.
 */
export function mergeFieldValueLabels(
    fields: LenseField[] | undefined,
    valueLabels: Record<string, Record<string, string>> | undefined,
): Record<string, Record<string, string>> | undefined {
    const merged: Record<string, Record<string, string>> = {};
    for (const field of fields ?? []) {
        if (field.value_labels && Object.keys(field.value_labels).length > 0) {
            merged[field.id] = { ...field.value_labels };
        }
    }
    for (const [key, labels] of Object.entries(valueLabels ?? {})) {
        merged[key] = { ...(merged[key] ?? {}), ...labels };
    }
    return Object.keys(merged).length > 0 ? merged : undefined;
}

/** A finite number, or null. A blank string is NOT 0 (`Number('')`). */
function toNumber(v: unknown): number | null {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const text = String(v).trim();
    if (text === '') return null;
    const n = Number(text);
    return Number.isFinite(n) ? n : null;
}

/** A boolean field's value as stored: a real boolean, or the `"True"`/`"false"` spelling the
 *  TEXT fact store (and Python's `str(True)`) leaves behind — case-folded. Anything else: null. */
function toBool(v: unknown): boolean | null {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
        const m = /^(true|false)$/i.exec(v.trim());
        if (m) return m[1].toLowerCase() === 'true';
    }
    return null;
}

export function formatEditValue(field: LenseField, value: unknown): string {
    if (value == null) return '';
    const kind = editInputKind(field);
    if (kind === 'number') {
        if (typeof value === 'string' && value.trim() === '') return '';
        const num = toNumber(value);
        if (num != null) {
            const plain = String(num);
            // Exponent form (`1e-7`, `1e+21`) is refused by parseEditValue, so spell it out.
            if (!/e/i.test(plain)) return plain.replace('.', ',');
            return num.toLocaleString('de-DE', { useGrouping: false, maximumFractionDigits: 20 });
        }
        return String(value);
    }
    if (kind === 'boolean') {
        const b = toBool(value);
        return b === true ? 'true' : b === false ? 'false' : '';
    }
    return String(value);
}

/** de-DE grammar (spec §7 A8). A `,` is the decimal mark and every `.` is grouping, and the
 *  groups must be well-formed. Without a `,`: dots are grouping only when every group after
 *  the first has exactly three digits and the integer part is not `0` (`1.234`, `33.300`,
 *  `1.234.567`); otherwise ONE dot is the decimal mark (`7.5`, `0.125`, `1.2345`) and two or
 *  more are malformed. Nothing is "repaired": `1..2`, `1,2.3`, `.5`, `5.` are refused. */
const GROUPED_INT = /^[-+]?(?:0|[1-9]\d{0,2})(?:\.\d{3})+$/;
const PLAIN = /^[-+]?\d+(?:\.\d+)?$/;
const GROUPED_WITH_COMMA = /^[-+]?(?:\d+|[1-9]\d{0,2}(?:\.\d{3})+),\d+$/;

function parseDeNumber(raw: string): number | null {
    const s = raw.replace(/\s/g, '');
    let normalised: string | null = null;
    if (s.includes(',')) {
        if (GROUPED_WITH_COMMA.test(s)) normalised = s.replace(/\./g, '').replace(',', '.');
    } else if (GROUPED_INT.test(s) && !/^[-+]?0\./.test(s)) {
        normalised = s.replace(/\./g, '');
    } else if (PLAIN.test(s)) {
        normalised = s;
    }
    if (normalised == null) return null;
    const num = Number(normalised);
    return Number.isFinite(num) ? num : null;
}

export function parseEditValue(field: LenseField, raw: string): ParseResult {
    const text = raw.trim();
    if (text === '') return { ok: false, reason: 'empty' };
    const kind = editInputKind(field);
    if (kind === 'select') {
        return field.values?.includes(text) ? { ok: true, value: text } : { ok: false, reason: 'not_allowed' };
    }
    if (kind === 'number') {
        const num = parseDeNumber(text);
        if (num == null) return { ok: false, reason: 'not_a_number' };
        if (INT_TYPES.has(field.type) && !Number.isInteger(num)) return { ok: false, reason: 'not_an_integer' };
        return { ok: true, value: num };
    }
    if (kind === 'date') {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
        if (!m) return { ok: false, reason: 'invalid_date' };
        const d = new Date(`${text}T00:00:00Z`);
        const valid = !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === text;
        return valid ? { ok: true, value: text } : { ok: false, reason: 'invalid_date' };
    }
    if (kind === 'boolean') {
        if (text === 'true') return { ok: true, value: true };
        if (text === 'false') return { ok: true, value: false };
        return { ok: false, reason: 'not_allowed' };
    }
    return { ok: true, value: text };
}

/** Equality for "has the refetched row caught up with my pending edit?" — SCHEMA-AWARE.
 *  Numeric coercion only where the ontology says the field is a number; a `customer_number`
 *  `"0012"` must never read as caught up by a `"12"` (codex r1 P0). */
export function valuesEqual(field: LenseField | undefined, a: unknown, b: unknown): boolean {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    const kind = field ? editInputKind(field) : 'text';
    if (kind === 'boolean') {
        const ba = toBool(a);
        const bb = toBool(b);
        return ba != null && bb != null ? ba === bb : a === b;
    }
    if (typeof a === 'boolean' || typeof b === 'boolean') return a === b;
    if (kind === 'number') {
        const na = toNumber(a);
        const nb = toNumber(b);
        return na != null && nb != null && na === nb;
    }
    if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim();
    return JSON.stringify(a) === JSON.stringify(b);
}
